import { all, spawn, takeLatest } from 'redux-saga/effects'
import { Action } from 'redux-actions'
import { App } from '@capacitor/app'
import { getWhoAmISuccess } from '../../../common/auth/authActions'
import { getChatClient } from '../../ChallengeChat/hooks/useInitChat'
import { getFullName } from '../../../common/helpers/translations'
import { OnboardingStatus, UserProfileDto } from '../../../services/api.types'
import { hasFeatureFlag } from '../../../common/helpers/featureFlags'
import { setChatUnreadCount } from './ChatActions'
import { getRelevantChatCids } from './ChatSelectors'
import { getCurrentCompanyGoalSuccess } from '../../Activity/store/ActivityActions'
import { IGlobalState } from '../../../Root/store/appReducer'
import { store } from '../../../index'

// The chat nav badge shows the unread count across the channels the chat view can show. The
// shared Stream client is connected right after auth so the badge is accurate before any chat
// surface mounts. The count is scoped to the app's channel set (getRelevantChatCids) — the raw
// Stream total would also include channels the user can't see here (ended/left chats, or other
// environments sharing the Stream app), producing unfindable phantom unreads.
// Native app Badge intentionally keeps using unreadNotificationsCount only (no chat).

let listenersAttachedFor: string | undefined
let requestRefresh: (() => void) | undefined

async function initChatUnread(userDto: UserProfileDto) {
  try {
    const { userId, firstName, lastName, email } = userDto
    const client = await getChatClient(userId, getFullName(firstName, lastName, email))
    // The singleton is never disconnected on logout; after re-login as a DIFFERENT user it may
    // still hold the old identity — bail out rather than show a wrong-user badge.
    if (!client || client.userID !== userId) return

    const refresh = async () => {
      if (client.userID !== userId) return
      const response = await client.getUnreadCount()
      const relevantCids = new Set(getRelevantChatCids(store.getState() as any as IGlobalState))
      const total = (response.channels || [])
        .filter((channel) => relevantCids.has(channel.channel_id))
        .reduce((sum, channel) => sum + (channel.unread_count || 0), 0)
      store.dispatch(setChatUnreadCount(total))
    }

    // Events carry only the global total, so re-query (throttled) instead of trusting them.
    let refreshTimer: ReturnType<typeof setTimeout> | undefined
    const scheduleRefresh = () => {
      if (refreshTimer) return
      refreshTimer = setTimeout(() => {
        refreshTimer = undefined
        refresh().catch((e) => console.error('chat unread refresh failed: ', e))
      }, 1500)
    }
    requestRefresh = scheduleRefresh

    if (listenersAttachedFor !== userId) {
      listenersAttachedFor = userId

      client.on('notification.message_new', scheduleRefresh)
      client.on('message.new', scheduleRefresh)
      client.on('notification.mark_read', scheduleRefresh)
      client.on('notification.mark_unread', scheduleRefresh)
      client.on('connection.recovered', scheduleRefresh)

      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) scheduleRefresh()
      })
    }

    await refresh()
  } catch (e: any) {
    // No badge, boot unaffected.
    console.error('initChatUnread failed: ', e)
  }
}

function* sagaWhoAmISuccessChat({ payload }: Action<any>) {
  const { authenticated, userDto, userCompanies } = payload || {}
  if (!authenticated || !userDto?.userId || userDto.onboardingStatus !== OnboardingStatus.DONE) return
  // The chat view (and its unread badge) is gated by the GROUP_CHAT company feature — don't
  // open a Stream connection for companies without it.
  if (!hasFeatureFlag(userCompanies, 'groupChat')) return
  // Detached: a Stream failure can never block or kill the boot flow.
  yield spawn(initChatUnread, userDto)
}

// The relevant channel set depends on the loaded goals — re-scope the badge when they arrive
// (matters after a fresh login, when the boot refresh ran against an empty goal list).
function sagaGoalsLoaded() {
  requestRefresh?.()
}

export function* chatSagas() {
  yield all([
    takeLatest(String(getWhoAmISuccess), sagaWhoAmISuccessChat),
    takeLatest(String(getCurrentCompanyGoalSuccess), sagaGoalsLoaded),
  ])
}
